import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useExecutionProgressTrackingsQuery } from 'hooks/queries/useExecutionProgressTrackingsQuery';
import { Flex, Line } from 'components';
import { FieldLabel, Radio, SwitchButton, TextField } from 'components/_form';
import { ColorPicker } from 'components/_form/ColorPicker/ColorPicker';
import { ErrorText } from 'components/_form/ErrorText';
import { NestedSelect, IOption as OptionForNestedSelect } from 'components/_form/NestedSelect/NestedSelect';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ITaskTypePayload } from 'types/taskType';
import {
  parentToChildrenAreasStructure,
  parseParentToChildrenAreasStructureToOptions
} from 'utilities/areas';
import { ApprovalSchemeApproversSection } from 'components/ApprovalSchemeApproversSection/ApprovalSchemeApproversSection';
import { TaskTypeBadge } from '../../TaskTypeBadge/TaskTypeBadge';
import { useTranslation } from 'react-i18next';
import { useAdminTaskTypeContext } from 'contexts/AdminTaskTypeContext';

interface Props {}

export const TaskTypeForm: React.FC<Props> = () => {
  const { data: areasData = [] } = useAreasQuery();
  const { data: executionProgressTrackings = [] } =
    useExecutionProgressTrackingsQuery();
  const { userFunctionsList } = useAdminTaskTypeContext();
  const areas = useMemo<OptionForNestedSelect[]>(
    () =>
      parseParentToChildrenAreasStructureToOptions(
        parentToChildrenAreasStructure(areasData)
      ),
    [areasData]
  );

  const { t } = useTranslation();

  const {
    register,
    control,
    watch,
    formState: { errors }
  } = useFormContext<ITaskTypePayload['task_type']>();

  const colorCode = watch('color_code');
  const colorIcon = watch('color_icon');
  const name = watch('name');
  const selectedTrackingId = watch('execution_progress_tracking_id');

  const approvalTrackings = useMemo(
    () =>
      executionProgressTrackings.filter(
        (tracking) => tracking.id === selectedTrackingId
      ),
    [executionProgressTrackings, selectedTrackingId]
  );

  return (
    <Flex flexDirection="column" gap="10px">
      <Flex alignItems="center" flexDirection={['column', 'row']}>
        <Flex width={['100%', '20%']}>
          <FieldLabel>
            {t('administrationTasksView.clickToChooseColor')}
          </FieldLabel>
        </Flex>
        <Flex width={['100%', '80%']} gap="16px" alignItems="center" flexWrap="wrap">
          <Controller
            control={control}
            name="color_code"
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                controlledDisplayColorPicker={value}
                onChange={onChange}
              />
            )}
          />
          <TaskTypeBadge
            color_code={colorCode}
            color_icon={colorIcon}
            name={name}
          />
        </Flex>
      </Flex>

      <Flex justifyContent="end" width="100%">
        <Flex width={['100%', '80%']} gap="10px" alignItems="end">
          <TextField
            {...register('color_icon')}
            width="80px"
            maxLength={3}
            label={t('administrationTasksView.code')}
            error={t(errors.color_icon?.message || '')}
          />
          <TextField
            {...register('name')}
            label={t('administrationTasksView.name')}
            error={t(errors.name?.message || '')}
          />
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <Flex flexDirection={['column', 'row']}>
        <Flex width={['100%', '20%']} flexDirection="column" gap="4px">
          <FieldLabel>{t('administrationTasksView.active?')}</FieldLabel>
          <Controller
            control={control}
            name="active"
            render={({ field: { onChange, value } }) => (
              <SwitchButton
                value={!!value}
                onChange={(checked) => {
                  onChange(checked);
                }}
              />
            )}
          />
        </Flex>
        <Flex width={['100%', '80%']}>
          <Controller
            control={control}
            name="areas"
            render={({ field: { onChange, value } }) => (
              <NestedSelect
                width="100%"
                label={t('administrationTasksView.usedInAreas')}
                helperText="common.leave blank if all company"
                selectedOptions={value ?? []}
                setSelectedOptions={onChange}
                options={areas}
              />
            )}
          />
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <Flex alignItems="center" flexDirection={['column', 'row']}>
        <Flex width={['100%', '20%']}>
          <FieldLabel>
            {t('administrationTasksView.followingStatusBy')}
          </FieldLabel>
        </Flex>
        <Flex width={['100%', '80%']} flexDirection="column">
          <Controller
            control={control}
            name="execution_progress_tracking_id"
            render={({ field: { value, onChange } }) => (
              <Flex gap="24px" alignItems="center" flexWrap="wrap">
                {executionProgressTrackings.map((executionProgressTracking) => (
                  <Flex
                    key={`TaskTypeForm-executionProgressTrackings-${executionProgressTracking.id}`}
                    alignItems="center"
                    gap="8px"
                  >
                    <Radio
                      checked={value === executionProgressTracking.id}
                      onClick={() => {
                        onChange(executionProgressTracking.id);
                      }}
                    />
                    <ExecutionProgressTracking
                      activeAll
                      executionProgressTracking={executionProgressTracking}
                    />
                  </Flex>
                ))}
              </Flex>
            )}
          />
          <ErrorText>
            {t(errors.execution_progress_tracking_id?.message || '')}
          </ErrorText>
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <ApprovalSchemeApproversSection
        showRequiresApproval
        selectedTrackingId={selectedTrackingId}
        executionProgressTrackings={approvalTrackings}
        userFunctionsList={userFunctionsList}
      />
    </Flex>
  );
};
